import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface MaintenanceModeSettings {
  enabled: boolean;
  message: string;
  startTime?: string;
  endTime?: string;
  scheduledMaintenance?: any[];
}

export function MaintenanceBanner() {
  const [isDismissed, setIsDismissed] = useState(false);

  // Get maintenance mode settings from public endpoint
  const { data: maintenanceSettings, isLoading } = useQuery<MaintenanceModeSettings>({
    queryKey: ['/api/maintenance-status'],
    enabled: true,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    retry: 2, // Retry on failure
  });

  // Reset dismissal when maintenance mode changes
  useEffect(() => {
    if (maintenanceSettings?.enabled) {
      setIsDismissed(false);
    }
  }, [maintenanceSettings?.enabled, maintenanceSettings?.message]);

  // Don't show if loading, not enabled, or dismissed
  if (isLoading || !maintenanceSettings?.enabled || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  return (
    <Alert 
      className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950 mb-4 rounded-lg"
      data-testid="maintenance-banner"
    >
      <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
      <AlertDescription className="flex items-center justify-between w-full">
        <div className="flex-1 pr-4">
          <span className="font-medium text-orange-800 dark:text-orange-200">
            System Maintenance:
          </span>
          <span className="ml-2 text-orange-700 dark:text-orange-300">
            {maintenanceSettings.message}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="h-auto p-1 text-orange-600 hover:text-orange-800 hover:bg-orange-100 dark:text-orange-400 dark:hover:text-orange-200 dark:hover:bg-orange-900 flex-shrink-0"
          data-testid="button-dismiss-maintenance"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Dismiss maintenance notification</span>
        </Button>
      </AlertDescription>
    </Alert>
  );
}